import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { fetchUserCanvasses } from '../../utils/canvasses';
import { fetchUserCompanies } from '../../utils/companies';
import { logger } from '../../utils/logger';
import styles from './ClientDashboard.module.css';
import Button from '../layout/buttons/Button';
import SummaryStats from '../charts/SummaryStats';
import SimpleBarChart from '../charts/SimpleBarChart';
import EmotionChart from '../charts/EmotionChart';
import { getAccessToken, makeAuthenticatedRequest } from '../../utils/reports';

const ClientDashboard = () => {
  const { accounts, instance } = useMsal();
  const navigate = useNavigate();
  const [userCanvasses, setUserCanvasses] = useState([]);
  const [companyCanvasses, setCompanyCanvasses] = useState([]);
  const [userCompanies, setUserCompanies] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  
  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load dashboard analytics data from report service
      try {
        const dashboardResult = await makeAuthenticatedRequest('/dashboard/data', {
          method: 'GET'
        });
        
        if (dashboardResult.success) {
          setDashboardData(dashboardResult.data);
          if(dashboardResult.data?.summary?.totalCanvasses === 0) {
            navigate('/welcome');
            return;
          }
        }
      } catch (error) {
        logger.error('Failed to fetch dashboard data:', error);
      }
      
      // Load user's personal canvasses (limited to first 3)
      const userCanvassesResult = await fetchUserCanvasses({ pageSize: 3 });
      setUserCanvasses(userCanvassesResult || []);

      // If the user has no canvasses, redirect them to the welcome/tutorial page
      if (!userCanvassesResult || userCanvassesResult.length === 0) {
        navigate('/welcome');
        return;
      }

      // Load user's companies
      const companiesResult = await fetchUserCompanies();
      if (companiesResult.success) {
        const companies = companiesResult.companies || [];
        setUserCompanies(companies);
        
        // Load canvasses for each company (limited to 3 per company)
        const companyCanvassesData = [];
        for (const company of companies) {
          try {
            const companyCanvassesResult = await fetchUserCanvasses({ companyId: company.id, pageSize: 3 });
            if (companyCanvassesResult && companyCanvassesResult.length > 0) {
              companyCanvassesData.push({
                company,
                canvasses: companyCanvassesResult
              });
            }
          } catch (err) {
            logger.error(`Error loading canvasses for company ${company.id}:`, err);
          }
        }
        setCompanyCanvasses(companyCanvassesData);
      }
    } catch (err) {
      logger.error('Error loading dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Render status using CSS module classes: outer pill + inner status modifier class
  const renderStatus = (status) => {
      const s = String(status || '').toLowerCase();
      let label = '—';
      // Map status to a CSS class key defined in Canvasses.module.css
      let classKey = '';
      
      switch (s) {
          case 'drafting':
          case 'draft':
              label = 'Draft';
              classKey = 'draft';
              break;
          case 'in_progress':
              label = 'Draft';
              classKey = 'draft';
              break;
          case 'approved':
              label = 'Pending Activation';
              classKey = 'approved';
              break;
          case 'active':
              label = 'Live';
              classKey = 'live';
              break;
          case 'complete':
              label = 'Complete';
              classKey = 'completed';
              break;
          default:
              label = s ? s.charAt(0).toUpperCase() + s.slice(1) : '—';
              classKey = '';
      }
      return (
          <div className={styles.statusSlot}>
              <div className={styles.statusContainer + ' ' + styles.statusBadge + ' ' + styles[classKey]}>
                  <div className={classKey ? styles[classKey] : undefined}>{label}</div>
              </div>
          </div>
      );
  };
  if (loading) {
    return (
      <div className={styles.dashboardContainer}>
        <div className={styles.loadingState}>
          <div className={styles.spinner}></div>
          <p>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.dashboardContainer}>
        <div className={styles.errorState}>
          <p>{error}</p>
          <Button onClick={loadDashboardData} variant="primary">Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.dashboardContainer}>
      <div className={styles.dashboardHeader}>
        <h2>{accounts[0]?.name || 'User'}'s Dashboard</h2>
        <p>Monitor your canvasses and company performance at a glance</p>
      </div>

      {/* Analytics Section */}
      {dashboardData && (
        <div className={styles.analyticsSection}>
          <div className={styles.sectionHeader}>
            <h3>Analytics Overview</h3>
            <Button 
              variant="outline-neutral" 
              size="sm" 
              onClick={loadDashboardData}
            >
              Refresh
            </Button>
          </div>

          {/* Summary Stats */}
          <SummaryStats summary={dashboardData.summary} />

          {/* Charts Grid */}
          <div className={styles.chartsGrid}>
            {/* Interview Frequency Chart */}
            {dashboardData.canvasses.some(c => c.interviewCount > 0) && (
              <SimpleBarChart
                title="Interviews per Canvass"
                data={dashboardData.canvasses.map(canvass => ({
                  name: canvass.canvassName || (canvass.objective.length > 30 
                    ? canvass.objective.substring(0, 30) + '...' 
                    : canvass.objective),
                  value: canvass.interviewCount,
                  color: 'var(--brand-as-blue-700)',
                  canvassId: canvass.canvassId
                }))}
                maxValue={Math.max(...dashboardData.canvasses.map(c => c.interviewCount), 1)}
              />
            )}

            {/* Completion Rate Chart */}
            {dashboardData.canvasses.some(c => c.performance && c.performance.completionRate > 0) && (
              <SimpleBarChart
                title="Completion Rates"
                data={dashboardData.canvasses
                  .filter(c => c.performance && c.performance.completionRate > 0)
                  .map(canvass => ({
                    name: canvass.canvassName || (canvass.objective.length > 30 
                      ? canvass.objective.substring(0, 30) + '...' 
                      : canvass.objective),
                    value: Math.round(canvass.performance.completionRate),
                    color: canvass.performance.completionRate >= 80 ? 'var(--brand-as-green-700)' : 
                           canvass.performance.completionRate >= 60 ? 'var(--brand-as-purple-700)' : 'var(--system-amber-600)',
                    canvassId: canvass.canvassId
                  }))}
                maxValue={100}
              />
            )}
          </div>

          {/* Emotions Chart */}
          {dashboardData.canvasses.some(c => c.topEmotions && c.topEmotions.length > 0) && (
            <EmotionChart
              title="Top Emotions Across All Canvasses"
              emotions={dashboardData.canvasses.flatMap(c => c.topEmotions || [])}
            />
          )}
        </div>
      )}

      {/* Personal Canvasses Section */}
      <div className={styles.dashboardSection}>
        <div className={styles.sectionHeader}>
          <h3>Your Personal Canvasses</h3>
          <Link to="/canvasses">
            <Button variant="outline-neutral" size="sm">View All</Button>
          </Link>
        </div>
        
        {userCanvasses.length === 0 ? (
          <div className={styles.emptyState}>
            <p>You don't have any personal canvasses yet.</p>
            <Link to="/canvasses">
              <Button variant="primary">Create Your First Canvass</Button>
            </Link>
          </div>
        ) : (
          <div className={styles.canvassesGrid}>
            {userCanvasses.map(canvass => (
              <div key={canvass.id} className={styles.canvassCard}>
                <div className={styles.canvassHeader}>
                  <h4>{canvass.canvassName}</h4>
                  {renderStatus(canvass.status)}
                </div>
                
                {canvass.canvassDescription && (
                  <p className={styles.canvassDescription}>{canvass.canvassDescription}</p>
                )}
                
                <div className={styles.canvassMeta}>
                  <span className={styles.createdDate}>
                    Created: {new Date(canvass.timestamp).toLocaleDateString()}
                  </span>
                  {(canvass.companyContext || canvass.stakeholderContext) && (
                    <span className={styles.contextInfo}>
                      Type: {canvass.companyContext ? 'Company' : 'Stakeholder'}
                    </span>
                  )}
                </div>
                
                <div className={styles.canvassActions}>
                  <Link to={`/canvass/${canvass.id}`}>
                    <Button variant="primary" size="sm">View Details</Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Company Canvasses Section */}
      {companyCanvasses.length > 0 && (
        <div className={styles.dashboardSection}>
          <div className={styles.sectionHeader}>
            <h3>Organization Canvasses</h3>
            <Link to="/canvasses">
              <Button variant="outline-neutral" size="sm">View All</Button>
            </Link>
          </div>
          
          <div className={styles.companySections}>
            {companyCanvasses.map(({ company, canvasses }) => (
              <div key={company.id} className={styles.companySection}>
                <div className={styles.companyHeader}>
                  <h4>{company.name}</h4>
                  <span className={styles.canvassCount}>{canvasses.length} canvasses</span>
                </div>
                
                <div className={styles.canvassesList}>
                  {canvasses.map(canvass => (
                    <div key={canvass.id} className={styles.canvassItem}>
                      <div className={styles.canvassInfo}>
                        <h5>{canvass.canvassName}</h5>
                        {renderStatus(canvass.status)}
                      </div>
                      
                      <div className={styles.canvassDetails}>
                        {canvass.canvassDescription && (
                          <p className={styles.canvassDescription}>{canvass.canvassDescription}</p>
                        )}
                        <span className={styles.createdDate}>
                          Created: {new Date(canvass.timestamp).toLocaleDateString()}
                        </span>
                      </div>
                      
                      <Link to={`/canvass/${canvass.id}`}>
                        <Button variant="outline-neutral" size="sm">View</Button>
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className={styles.quickActions}>
        <h3>Quick Actions</h3>
        <div className={styles.actionButtons}>
          <Link to="/canvasses">
            <Button variant="primary">Create New Canvass</Button>
          </Link>
          <Link to="/canvasses">
            <Button variant="outline-neutral">Browse All Canvasses</Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ClientDashboard;
